import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../stores/authStore';
import type { ResolvedFeatureFlags } from '../domains/feature-flags/models/FeatureFlags';
import {
  canRunProcessExtraction,
  canShowProcessExtractionUx,
} from '../domains/feature-flags/models/FeatureFlags';

interface FeatureFlagsResponse {
  success: boolean;
  data?: ResolvedFeatureFlags;
  error?: string;
}

export function useFeatureFlags() {
  const { isAuthenticated } = useAuthStore();

  const { data: flags, isLoading: loading, error, refetch } = useQuery<ResolvedFeatureFlags>({
    queryKey: ['feature-flags'],
    enabled: isAuthenticated,
    queryFn: async () => {
      const response = await fetch('/api/me/feature-flags', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      const data: FeatureFlagsResponse = await response.json();
      if (data.success && data.data) {
        return data.data;
      }
      throw new Error(data.error || 'Failed to fetch feature flags');
    },
    staleTime: 60_000,
    retry: 2,
  });

  const canRunProcessExtractionFlag = useCallback(() => {
    if (!flags) return false;
    return canRunProcessExtraction(
      flags.modes.PROCESS_EXTRACTION_MODE,
      flags.flags.PROCESS_EXTRACTION_ENABLED,
    );
  }, [flags]);

  const canShowProcessExtractionUxFlag = useCallback(() => {
    if (!flags) return false;
    return canShowProcessExtractionUx(
      flags.modes.PROCESS_EXTRACTION_MODE,
      flags.flags.PROCESS_EXTRACTION_ENABLED,
    );
  }, [flags]);

  const isRdmAssistantWriteEnabled = useCallback(
    () => flags?.flags.RDM_ASSISTANT_WRITE ?? false,
    [flags],
  );

  const isKnowledgeIngestionEnabled = useCallback(
    () => flags?.flags.KNOWLEDGE_INGESTION ?? false,
    [flags],
  );

  const isProcessRepositoryEnabled = useCallback(
    () => flags?.flags.PROCESS_REPOSITORY ?? false,
    [flags],
  );

  const isBlueprintEnabled = useCallback(
    () => flags?.flags.BLUEPRINT_ENABLED ?? false,
    [flags],
  );

  const isRdmImportExportEnabled = useCallback(
    () => flags?.flags.RDM_IMPORT_EXPORT ?? false,
    [flags],
  );

  const isCompassEnabled = useCallback(
    () => flags?.flags.COMPASS_ENABLED ?? false,
    [flags],
  );

  const isAddOnOverlayEnabled = useCallback(
    () => flags?.flags.ADDON_OVERLAY ?? false,
    [flags],
  );

  const isAtlasWorkloadPlanningEnabled = useCallback(
    () => flags?.flags.ATLAS_WORKLOAD_PLANNING ?? false,
    [flags],
  );

  const showProcessExtractionUx = !loading && canShowProcessExtractionUxFlag();

  return {
    flags,
    loading,
    error: error?.message ?? null,
    refetch,
    showProcessExtractionUx,
    canRunProcessExtraction: canRunProcessExtractionFlag,
    canShowProcessExtractionUx: canShowProcessExtractionUxFlag,
    isRdmAssistantWriteEnabled,
    isKnowledgeIngestionEnabled,
    isProcessRepositoryEnabled,
    isBlueprintEnabled,
    isRdmImportExportEnabled,
    isCompassEnabled,
    isAddOnOverlayEnabled,
    isAtlasWorkloadPlanningEnabled,
    isBlueprintHostedSyncEnabled: isBlueprintEnabled,
  };
}
